import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errorMessage } from "../lib/api";
import { PageHeader, ActionButton, GhostButton, PillBadge } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { Sparkles, Trash2, Users, AlertTriangle, PenSquare, Save, Lock } from "lucide-react";

const SYSTEM_TONE = { system: "amber", custom: "blue" };

/**
 * Portal Templates admin page — CRUD for agency-scoped custom templates and
 * "Apply to all clients" action for both system + custom presets. System
 * presets are read-only (delete blocked, edit blocked) so agency admins
 * can&apos;t break the ship.
 */
export default function PortalTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [confirm, setConfirm]     = useState(null);       // { template, mode: "delete"|"apply-all" }
  const [busy, setBusy]           = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/portal/config-templates");
      setTemplates(data?.templates || []);
    } catch (err) {
      toast.error(errorMessage(err, "Failed to load templates"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (tpl) => {
    setBusy(true);
    try {
      await api.delete(`/portal/config-templates/${tpl.id}`);
      toast.success(`Deleted "${tpl.name}"`);
      setConfirm(null);
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Delete failed"));
    } finally {
      setBusy(false);
    }
  };

  const applyToAll = async (tpl) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/portal/config-templates/${tpl.id}/apply`, {
        apply_to_all: true,
      });
      toast.success(`Applied "${tpl.name}" to ${data.count} client${data.count === 1 ? "" : "s"}`);
      setConfirm(null);
    } catch (err) {
      toast.error(errorMessage(err, "Apply-to-all failed"));
    } finally {
      setBusy(false);
    }
  };

  const system = templates.filter((t) => t.is_system);
  const custom = templates.filter((t) => !t.is_system);

  return (
    <div data-testid="portal-templates-page">
      <PageHeader
        title="Portal Templates"
        subtitle="Vertical presets that shape the client-facing dashboard layout. Apply a template to a single client (from their config page) or to every client at once."
      />

      <div className="p-8 space-y-10">
        {loading ? (
          <div className="text-zinc-400 text-sm">Loading templates…</div>
        ) : (
          <>
            <Section
              title="System presets"
              subtitle="Six read-only presets that ship with every agency."
              tone="system"
              templates={system}
              onApplyAll={(t) => setConfirm({ template: t, mode: "apply-all" })}
            />

            <Section
              title="Your saved templates"
              subtitle="Templates you saved from a client's config page. Delete or apply to every client."
              tone="custom"
              templates={custom}
              onApplyAll={(t) => setConfirm({ template: t, mode: "apply-all" })}
              onDelete={(t) => setConfirm({ template: t, mode: "delete" })}
              emptyHint="No custom templates yet. Head to any client's portal config and click Save as template."
            />
          </>
        )}
      </div>

      {confirm && (
        <ConfirmModal
          template={confirm.template}
          mode={confirm.mode}
          busy={busy}
          onClose={() => setConfirm(null)}
          onConfirm={() => (confirm.mode === "delete" ? remove(confirm.template) : applyToAll(confirm.template))}
        />
      )}
    </div>
  );
}


function Section({ title, subtitle, tone, templates, onApplyAll, onDelete, emptyHint }) {
  return (
    <section className="space-y-4" data-testid={`portal-templates-section-${tone}`}>
      <div>
        <div className="flex items-center gap-2 mb-1">
          <h2 className="font-display uppercase font-bold text-2xl tracking-tight">{title}</h2>
          <PillBadge tone={SYSTEM_TONE[tone] || "default"}>{tone === "system" ? "READ-ONLY" : "CUSTOM"}</PillBadge>
        </div>
        {subtitle && <p className="text-zinc-500 text-sm">{subtitle}</p>}
      </div>

      {templates.length === 0 ? (
        <div className="border border-dashed border-[#27272A] p-6 text-zinc-500 text-sm text-center">
          {emptyHint || "No templates yet."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t) => (
            <div
              key={t.id}
              data-testid={`portal-template-card-${t.id}`}
              className="border border-[#27272A] bg-[#121212] p-5 flex flex-col gap-3 hover:border-[#3F3F46] transition-colors"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Sparkles className="size-4 text-[#FF3B30] shrink-0" />
                    <div className="font-display font-bold uppercase tracking-tight text-lg truncate">
                      {t.name}
                    </div>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 font-mono">
                    {t.vertical} · {t.snapshot?.metrics?.filter?.((m) => m.enabled).length || 0} enabled metrics
                  </div>
                </div>
                {t.is_system && <Lock className="size-3.5 text-zinc-500 shrink-0" title="System preset" />}
              </div>

              {t.description && (
                <p className="text-sm text-zinc-400 line-clamp-3">{t.description}</p>
              )}

              <div className="pt-3 mt-auto border-t border-[#27272A] flex items-center gap-2 flex-wrap">
                <ActionButton
                  data-testid={`portal-template-apply-all-${t.id}`}
                  onClick={() => onApplyAll(t)}
                >
                  <Users className="size-4" /> Apply to all clients
                </ActionButton>
                {onDelete && !t.is_system && (
                  <GhostButton
                    data-testid={`portal-template-delete-${t.id}`}
                    onClick={() => onDelete(t)}
                  >
                    <Trash2 className="size-4" /> Delete
                  </GhostButton>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


function ConfirmModal({ template, mode, busy, onClose, onConfirm }) {
  const isDelete = mode === "delete";
  return (
    <Modal
      onClose={onClose}
      title={isDelete ? "Delete template?" : "Apply to every client?"}
      testId="portal-templates-confirm-modal"
    >
      <div className="space-y-4">
        <div className={`flex items-start gap-3 border p-3 ${isDelete ? "border-[#FF3B30]/40 bg-[#FF3B30]/5" : "border-amber-500/40 bg-amber-500/5"}`}>
          {isDelete ? (
            <Trash2 className="size-5 text-[#FF3B30] shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="size-5 text-amber-400 shrink-0 mt-0.5" />
          )}
          <div className="text-sm text-zinc-300">
            <div className="font-bold text-zinc-100">{template.name}</div>
            <div className="mt-1">
              {isDelete
                ? "This custom template will be removed. Clients that had it applied stay untouched — you're just deleting the preset itself."
                : "Every client in this agency will get this template's layout (metric visibility, order, section labels). Per-metric labels, thresholds, and approval overrides on each client are preserved. This runs immediately and cannot be undone in one click."}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#27272A]">
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <ActionButton
            data-testid="portal-templates-confirm-btn"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? "Working…" : isDelete ? "Delete template" : "Apply to all clients"}
          </ActionButton>
        </div>
      </div>
    </Modal>
  );
}

// Keep the PenSquare + Save imports so the linter doesn't complain if they get
// removed later — they're placeholders for the upcoming inline-edit feature.
void PenSquare; void Save;
